import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { getProgressDebug } from "@/services/student/level-test-progress.service";
import type { LevelTestProgressDebug } from "@/services/student/level-test-progress.interfaces";

// Estado do contexto
interface LevelTestProgressState {
  debug?: LevelTestProgressDebug;
  loading: boolean;
  error?: string;
}

// Contexto
interface LevelTestProgressContextType {
  state: LevelTestProgressState;
  refresh: (sessionId: string) => Promise<void>;
  setMock: (debug: LevelTestProgressDebug) => void;
}

const LevelTestProgressContext = createContext<LevelTestProgressContextType | null>(null);

// Estado inicial
const initialState: LevelTestProgressState = {
  debug: undefined,
  loading: false,
  error: undefined,
};

// Provider
interface LevelTestProgressProviderProps {
  children: ReactNode;
}

export const LevelTestProgressProvider: React.FC<LevelTestProgressProviderProps> = ({
  children,
}) => {
  const [state, setState] = useState<LevelTestProgressState>(initialState);

  const refresh = useCallback(async (sessionId: string) => {
    // Nota: O backend já protege o endpoint via LevelTest:DebugEnabled
    // Removida verificação de VITE_DEBUG_PANEL_ENABLED para permitir chamadas
    // O backend retornará 404 se DebugEnabled estiver false na configuração

    if (!sessionId) {
      console.warn('[LevelTestProgressContext] refresh called without sessionId');
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: undefined }));

    try {
      const debug = await getProgressDebug({ sessionId });
      setState({
        debug,
        loading: false,
        error: undefined,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao buscar dados de debug';
      console.error('[LevelTestProgressContext] Error refreshing progress debug:', error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
        // Manter último snapshot em caso de erro
      }));
    }
  }, []);

  const setMock = useCallback((debug: LevelTestProgressDebug) => {
    setState({
      debug,
      loading: false,
      error: undefined,
    });
  }, []);

  const value: LevelTestProgressContextType = {
    state,
    refresh,
    setMock,
  };

  return (
    <LevelTestProgressContext.Provider value={value}>
      {children}
    </LevelTestProgressContext.Provider>
  );
};

// Hook de consumo
export const useLevelTestProgress = (): LevelTestProgressContextType => {
  const context = useContext(LevelTestProgressContext);
  if (!context) {
    throw new Error(
      'useLevelTestProgress must be used within a LevelTestProgressProvider'
    );
  }
  return context;
};

